import { gql, useMutation } from '@apollo/client';
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { trackEvent } from 'analytics'
import { useSites } from 'store'
import { toast } from 'components/global/CUK/Toast/Toast'

import { Device } from './types'
import { OPERATION_SITE } from './use-tree'

const OPERATION_UPDATE = gql`
  mutation UpdateCartographyAPsZones($sf_id: ID!, $uuids: [ID!], $enabled: Boolean!) {
    devices: updateCartographyAPsZones(sf_id: $sf_id, uuids: $uuids, enabled: $enabled) {
      uuid
      radio_enabled
    }
  }
`

export type RadioActionScope = 'device' | 'floor'

export type RadioActionSource = 'tree' | 'floor_plan'

type UpdateParams = {
  devices: Pick<Device, 'uuid' | 'connected_stations'>[]
  enabled: boolean
  scope: RadioActionScope
  source: RadioActionSource
}

export type UseDevice = {
  loading: boolean
  update: (params: UpdateParams) => Promise<void>
  uuids: string[]
}

const useAction = (): UseDevice => {
  const [uuids, setUuids] = useState<string[]>([])
  const { t } = useTranslation()
  const [{selected}] = useSites()
  const [mutate, { loading }] = useMutation(OPERATION_UPDATE, {
    variables: {
      sf_id: selected[0]?.sf_id,
    },
    update: (cache, { data }) => {
      cache.updateQuery(
        {
          query: OPERATION_SITE,
          variables: {
            sf_id: selected[0].sf_id,
          },
        },
        (oldData) => {
          const surfaces = oldData.surface_areas.map(surface => {
            const maps = surface.maps.map(map => {
              const devices = map.devices.map(device => {
                const newDevice = data.devices.find(({uuid}) => uuid === device.uuid)
                if (newDevice) {
                  return {
                    ...device,
                    radio_enabled: newDevice.radio_enabled
                  }
                }
                return device
              })
              return {
                ...map,
                devices
              }
            })
            return {
              ...surface,
              maps,
            }
          })
          return { surface_areas: surfaces }
        }
      )
    }
  })
  
  const update = async ({ devices, enabled, scope, source }: UpdateParams): Promise<void> => {
    const ids = devices.map(({uuid}) => uuid)
    setUuids(ids)
    try {
      await mutate({
        variables: {
          uuids: ids,
          enabled
        }
      })
      toast.success(t(`access_point_zones.toast.${enabled ? 'enabled' : 'disabled'}_${ids.length === 1 ? 'device' : 'map'}_success`))
      trackEvent(enabled ? 'access_point_zones_radio_enabled' : 'access_point_zones_radio_disabled', {
        scope,
        source,
        device_count: ids.length,
        connected_stations: devices.reduce((total, { connected_stations }) => total + (connected_stations || 0), 0)
      })
    } catch (e) {
      toast.error(t('actions.error'))
      trackEvent('access_point_zones_radio_failed', {
        enabled,
        scope,
        source,
        device_count: ids.length
      })
    } finally {
      setUuids([])
    }
  }

  return {
    loading,
    update,
    uuids
  }
}

export default useAction